import { createContext, useState, useEffect, useCallback, useMemo, useContext, useRef } from 'react';
import { SapiensAuthClient } from '@sapiens/auth-core';
export { ALLOWED_ORIGINS, SapiensAuthClient, TOKEN_EXPIRY_BUFFER } from '@sapiens/auth-core';
import { jsx } from 'react/jsx-runtime';

// src/provider.tsx
var SapiensAuthContext = createContext(null);
function SapiensAuthProvider({
  config,
  children,
  onAutoLoginStart,
  onAutoLoginSuccess,
  onAutoLoginError
}) {
  const [client] = useState(() => new SapiensAuthClient({
    ...config,
    onAutoLoginStart,
    onAutoLoginSuccess,
    onAutoLoginError
  }));
  const [user, setUser] = useState(() => client.getUser());
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    const unsubscribe = client.onAuthStateChange((newUser) => {
      setUser(newUser);
      setIsLoading(false);
    });
    const initAuth = async () => {
      const currentUser = client.getUser();
      if (currentUser) {
        setUser(currentUser);
        setIsLoading(false);
      } else if (client.hasStoredCredentials()) {
        await client.autoLogin();
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    };
    initAuth();
    return unsubscribe;
  }, [client]);
  const login = useCallback(
    (credentials) => client.login(credentials),
    [client]
  );
  const loginWithPopup = useCallback(
    (options) => client.loginWithPopup(options),
    [client]
  );
  const logout = useCallback(() => client.logout(), [client]);
  const getToken = useCallback(() => client.getToken(), [client]);
  const saveCredentials = useCallback(
    (email, password) => client.saveCredentials(email, password),
    [client]
  );
  const clearCredentials = useCallback(() => client.clearCredentials(), [client]);
  const value = useMemo(
    () => ({
      user,
      isAuthenticated: user !== null,
      isLoading,
      login,
      loginWithPopup,
      logout,
      getToken,
      saveCredentials,
      clearCredentials,
      hasStoredCredentials: client.hasStoredCredentials(),
      client
    }),
    [user, isLoading, login, loginWithPopup, logout, getToken, saveCredentials, clearCredentials, client]
  );
  return /* @__PURE__ */ jsx(SapiensAuthContext.Provider, { value, children });
}
function useSapiensAuth() {
  const context = useContext(SapiensAuthContext);
  if (!context) {
    throw new Error("useSapiensAuth must be used within a SapiensAuthProvider");
  }
  return context;
}
function useRequireRole(requiredUserType) {
  const { user, isLoading } = useSapiensAuth();
  const hasRole = useMemo(() => {
    if (!user) return false;
    if (requiredUserType === 2) {
      return user.userType === 2;
    }
    if (requiredUserType === 1) {
      return user.userType === 1 || user.userType === 2;
    }
    return false;
  }, [user, requiredUserType]);
  return { hasRole, isLoading, user };
}
function useAutoLogin(options = {}) {
  const { checkInterval = 3e4, onStart, onSuccess, onError } = options;
  const { client, isAuthenticated, hasStoredCredentials } = useSapiensAuth();
  const isAutoLoginInProgress = useRef(false);
  const attemptAutoLogin = useCallback(async () => {
    if (isAutoLoginInProgress.current) return;
    if (!hasStoredCredentials) return;
    if (!client.isTokenExpiringSoon()) return;
    isAutoLoginInProgress.current = true;
    onStart?.();
    try {
      const result = await client.autoLogin();
      if (result) {
        onSuccess?.();
      }
    } catch (error) {
      onError?.(error);
    } finally {
      isAutoLoginInProgress.current = false;
    }
  }, [client, hasStoredCredentials, onStart, onSuccess, onError]);
  useEffect(() => {
    if (!isAuthenticated) return;
    attemptAutoLogin();
    const intervalId = setInterval(() => {
      attemptAutoLogin();
    }, checkInterval);
    return () => {
      clearInterval(intervalId);
    };
  }, [isAuthenticated, attemptAutoLogin, checkInterval]);
}
function useToken() {
  const { client, isAuthenticated } = useSapiensAuth();
  const getToken = useCallback(async () => {
    const currentToken = client.getToken();
    if (!currentToken) {
      return null;
    }
    if (client.isTokenExpiringSoon() && client.hasStoredCredentials()) {
      const result = await client.autoLogin();
      return result?.token ?? currentToken;
    }
    return currentToken;
  }, [client]);
  return {
    getToken,
    token: isAuthenticated ? client.getToken() : null
  };
}

export { SapiensAuthProvider, useAutoLogin, useRequireRole, useSapiensAuth, useToken };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map